/**
 * `renderCampaignPlayer` — campaign playback primitive (POL-584).
 *
 * Walks a voter through every polst in a campaign, shows progress,
 * cross-fades between steps, and ends with a summary screen showing
 * the voter's choices alongside the overall tallies.
 *
 * Built on top of `renderPolstCard` (POL-583). The card renders the
 * current step; we re-mount a fresh card per step so the optimistic
 * vote state machine starts clean every time.
 *
 * Implementation notes:
 *   - The player owns its own `<section data-polst-campaign>` element
 *     inside the shadow widget container. Inside it, a progress shell
 *     (title + bar + step counter) sits above a `.polst-campaign__stage`
 *     element that hosts the current card OR the summary screen.
 *   - Progress is fetched once on mount via an injectable
 *     `fetchProgress` callback (defaults to `getCampaignProgress`).
 *     A rejection falls back to step 0 with a banner; the player
 *     still plays.
 *   - Votes route through an injectable `vote(polstId, option)`
 *     callback (defaults to `voteCampaign(campaignId, polstId, option)`).
 *   - Cross-fade uses two opacity transitions so the outgoing card
 *     fades while the incoming card fades in. `prefers-reduced-motion`
 *     skips the fade.
 */
import { voteCampaign as defaultVoteCampaign, getCampaignProgress } from "./api";
import {
  createLiveRegion,
  focusFirstInteractive,
  prefersReducedMotion,
  type LiveRegionHandle,
} from "./a11y";
import { getAppOrigin } from "./config";
import {
  renderPolstCard,
  type PolstCardDTO,
  type PolstHandle,
} from "./polst-card";

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Minimal campaign DTO shape the player consumes. Tracks the public
 * backend CampaignDTO (see `apps/backend/src/rest/mappers/types.ts`).
 */
export interface CampaignPlayerDTO {
  campaignId: string;
  title: string;
  description: string | null;
  status: "scheduled" | "open" | "closed";
  startsAt: string | null;
  endsAt: string | null;
  polsts: PolstCardDTO[];
  totalSteps: number;
  brand: { slug: string; name: string } | null;
}

/** Progress shape returned by `/api/rest/v1/campaigns/{id}/progress`. */
export interface CampaignPlayerProgress {
  campaignId: string;
  completedSteps: number;
  totalSteps: number;
  currentPolst: PolstCardDTO | null;
  completed: boolean;
  summary: {
    campaignId: string;
    totalSteps: number;
    completedAt: string;
  } | null;
}

export type CampaignPlayerStatus =
  | "loading"
  | "playing"
  | "awaitingNext"
  | "complete"
  | "closed"
  | "error";

export interface CampaignPlayerState {
  status: CampaignPlayerStatus;
  stepIndex: number;
  totalSteps: number;
  completedSteps: number;
  /** Populated while `status === "error"`. */
  error: unknown;
}

export interface CampaignStepVoteEvent {
  campaignId: string;
  polstId: string;
  option: "A" | "B";
  stepIndex: number;
  totalSteps: number;
  completedSteps: number;
}

export interface CampaignCompleteEvent {
  campaignId: string;
  totalSteps: number;
  completedAt: string;
  choices: Array<{ polstId: string; option: "A" | "B" | null }>;
}

export interface CampaignPlayerOptions {
  /**
   * Override the progress fetcher. Defaults to `getCampaignProgress` from
   * `./api`. Tests inject mocks here.
   */
  fetchProgress?: (campaignId: string) => Promise<CampaignPlayerProgress>;
  /**
   * Override the vote callback. Receives the polst short id + option.
   * Defaults to a closure around `voteCampaign(campaignId, polstId, option)`.
   */
  vote?: (polstId: string, option: "A" | "B") => Promise<unknown>;
  /**
   * Auto-advance to the next polst on successful vote. Default: true.
   * When false, the player parks on a "Next" button after each vote.
   */
  autoAdvance?: boolean;
  /** Fires after every successful step vote. */
  onStepVote?: (event: CampaignStepVoteEvent) => void;
  /** Fires once the campaign enters its summary screen. */
  onComplete?: (event: CampaignCompleteEvent) => void;
  /**
   * Fires when `fetchProgress` rejects. Defaults to a silent noop — the
   * player falls back to step 0 regardless.
   */
  onProgressError?: (err: unknown) => void;
  /** Hide the "by <brand>" attribution on the per-step cards. */
  hideBrand?: boolean;
  /** Hide the share button on the per-step cards. */
  hideShare?: boolean;
  /** Document override for deterministic rendering (mirrors the card). */
  document?: Document;
  /** Clock override for summary `completedAt` when absent. */
  now?: () => Date;
  /** Disable cross-fade animation (tests use this; reduced-motion also wins). */
  disableTransitions?: boolean;
}

export interface CampaignPlayerHandle {
  /** Snapshot of the current player state. */
  getState(): CampaignPlayerState;
  /**
   * Jump to any step by index. Useful for revoting a previous polst.
   * `completedSteps` is not decremented — the backend keeps the latest
   * vote-per-polst, so the total completed count stands.
   */
  goTo(stepIndex: number): void;
  /**
   * Feed a fresh campaign DTO. Lets embedders react to server pushes
   * (e.g. a campaign closing mid-playback). Progress is preserved.
   */
  update(campaign: CampaignPlayerDTO): void;
  /** Detach the player; subsequent handle calls are no-ops. */
  destroy(): void;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const CAMPAIGN_CSS = `
.polst-campaign {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-md);
  padding: var(--polst-spacing-lg);
  background: var(--polst-bg);
  color: var(--polst-fg);
  border-radius: var(--polst-radius);
  width: 100%;
  box-sizing: border-box;
}
.polst-campaign__header {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-xs);
}
.polst-campaign__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
}
.polst-campaign__description {
  margin: 0;
  font-size: 14px;
  color: var(--polst-muted-fg);
}
.polst-campaign__progress {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-xs);
}
.polst-campaign__progress-bar {
  position: relative;
  height: 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--polst-fg) 12%, transparent);
  overflow: hidden;
}
.polst-campaign__progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--polst-accent);
  border-radius: 999px;
  transition: width 180ms ease-out;
}
.polst-campaign__progress-label {
  font-size: 12px;
  color: var(--polst-muted-fg);
  display: flex;
  justify-content: space-between;
}
.polst-campaign__stage {
  position: relative;
  min-height: 100px;
}
.polst-campaign__card-layer {
  transition: opacity 150ms ease-out;
}
.polst-campaign__card-layer[data-transitioning="true"] {
  opacity: 0;
}
.polst-campaign[data-reduced-motion="true"] .polst-campaign__card-layer {
  transition: none;
}
.polst-campaign__next {
  align-self: flex-end;
  background: var(--polst-accent);
  color: var(--polst-accent-fg);
  border: 0;
  border-radius: var(--polst-radius);
  padding: var(--polst-spacing-sm) var(--polst-spacing-md);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.polst-campaign__next:hover { background: var(--polst-button-hover); }
.polst-campaign__next:focus-visible {
  outline: 2px solid var(--polst-accent);
  outline-offset: 2px;
}
.polst-campaign__banner {
  padding: var(--polst-spacing-sm) var(--polst-spacing-md);
  border-radius: var(--polst-radius);
  background: color-mix(in srgb, var(--polst-accent) 18%, transparent);
  font-size: 13px;
}
.polst-campaign__banner--warn {
  background: color-mix(in srgb, #f59e0b 22%, transparent);
}
.polst-campaign__summary {
  display: flex;
  flex-direction: column;
  gap: var(--polst-spacing-md);
}
.polst-campaign__summary-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}
.polst-campaign__summary-row {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  align-items: center;
  gap: var(--polst-spacing-sm);
  padding: var(--polst-spacing-sm);
  border-radius: var(--polst-radius);
  border: 1px solid color-mix(in srgb, var(--polst-fg) 12%, transparent);
}
.polst-campaign__summary-index {
  font-size: 12px;
  color: var(--polst-muted-fg);
  font-weight: 600;
}
.polst-campaign__summary-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.polst-campaign__summary-polst-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.polst-campaign__summary-choice {
  margin: 0;
  font-size: 12px;
  color: var(--polst-muted-fg);
}
.polst-campaign__summary-choice--skipped {
  font-style: italic;
  color: var(--polst-muted-fg);
}
.polst-campaign__summary-tally {
  font-size: 12px;
  color: var(--polst-muted-fg);
  text-align: right;
}
.polst-campaign__share {
  align-self: flex-start;
  background: var(--polst-accent);
  color: var(--polst-accent-fg);
  border: 0;
  border-radius: var(--polst-radius);
  padding: var(--polst-spacing-sm) var(--polst-spacing-md);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.polst-campaign__share:focus-visible {
  outline: 2px solid var(--polst-accent);
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .polst-campaign__progress-fill { transition: none !important; }
  .polst-campaign__card-layer { transition: none !important; }
  .polst-campaign * { animation: none !important; }
}
`;

const CAMPAIGN_STYLE_MARKER = "data-polst-campaign-styles";

function ensureStyles(root: ShadowRoot, doc: Document): void {
  if (root.querySelector(`style[${CAMPAIGN_STYLE_MARKER}]`)) return;
  const styleEl = doc.createElement("style");
  styleEl.setAttribute(CAMPAIGN_STYLE_MARKER, "");
  styleEl.textContent = CAMPAIGN_CSS;
  root.appendChild(styleEl);
}

function findWidgetContainer(root: ShadowRoot): Element {
  const container = root.querySelector("[data-polst-widget-root]");
  if (container) return container;
  return root as unknown as Element;
}

function percent(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return Math.max(0, Math.min(100, Math.round((numerator / denominator) * 100)));
}

// ─── Implementation ─────────────────────────────────────────────────────────

export function renderCampaignPlayer(
  root: ShadowRoot,
  campaign: CampaignPlayerDTO,
  options: CampaignPlayerOptions = {},
): CampaignPlayerHandle {
  if (!root || typeof (root as unknown as { appendChild?: unknown }).appendChild !== "function") {
    throw new Error("renderCampaignPlayer(): root must be a ShadowRoot");
  }
  if (!campaign || typeof campaign.campaignId !== "string") {
    throw new Error("renderCampaignPlayer(): campaign.campaignId is required");
  }

  const doc =
    options.document ??
    ((root as unknown as { ownerDocument?: Document }).ownerDocument as Document);
  if (!doc) {
    throw new Error("renderCampaignPlayer(): unable to resolve Document from root");
  }

  const autoAdvance = options.autoAdvance !== false;
  const now = options.now ?? (() => new Date());
  const reducedMotion = options.disableTransitions || prefersReducedMotion(doc);

  // Resolve the campaign-scoped vote callback. If the caller passed one
  // explicitly we use it verbatim; otherwise we close over the campaign
  // id so `voteCampaign(campaignId, polstId, option)` is driven per step.
  const voteStep =
    options.vote ??
    ((polstId: string, option: "A" | "B") =>
      defaultVoteCampaign(campaign.campaignId, polstId, option));

  // Progress resolver (injectable for tests). The default REST helper
  // returns a loose `CampaignProgress` (open record); we cast to the
  // richer shape the player consumes — the server schema matches
  // CampaignPlayerProgress per apps/backend/src/rest/mappers/types.ts.
  const fetchProgress: (id: string) => Promise<CampaignPlayerProgress> =
    options.fetchProgress ??
    ((id: string) =>
      getCampaignProgress(id).then((p) => p as unknown as CampaignPlayerProgress));

  ensureStyles(root, doc);
  const widgetContainer = findWidgetContainer(root);

  // ─── Mutable model ──────────────────────────────────────────────────────

  let current = cloneCampaign(campaign);
  let state: CampaignPlayerState = {
    status: "loading",
    stepIndex: 0,
    totalSteps: current.polsts.length,
    completedSteps: 0,
    error: null,
  };
  let destroyed = false;
  let cardHandle: PolstHandle | null = null;
  /** completed set (polstShortIds) — drives progress + resume + "goTo stays counted". */
  const completedPolstIds = new Set<string>();
  /** Voter's per-polst choice — survives revote so the summary always reflects the latest pick. */
  const choices = new Map<string, "A" | "B">();

  const sectionEl = doc.createElement("section");
  sectionEl.className = "polst-campaign";
  sectionEl.setAttribute("data-campaign-id", current.campaignId);
  sectionEl.setAttribute("role", "region");
  const campaignTitleId = `polst-campaign-${current.campaignId}-title`;
  sectionEl.setAttribute("aria-labelledby", campaignTitleId);
  if (reducedMotion) sectionEl.setAttribute("data-reduced-motion", "true");

  // One visually-hidden polite live region per player. We announce
  // step transitions ("Step 3 of 5. <Title>.") and the final summary.
  const liveRegion: LiveRegionHandle = createLiveRegion(doc);
  sectionEl.appendChild(liveRegion.el);
  // Tracks whether the very next `mountStepCard` should steal focus.
  // We only steal after an advance, never on the initial mount.
  let focusOnNextMount = false;

  // Header — title + description + progress bar.
  const headerEl = doc.createElement("div");
  headerEl.className = "polst-campaign__header";
  const titleEl = doc.createElement("h2");
  titleEl.className = "polst-campaign__title";
  titleEl.id = campaignTitleId;
  titleEl.textContent = current.title;
  headerEl.appendChild(titleEl);
  if (current.description) {
    const descEl = doc.createElement("p");
    descEl.className = "polst-campaign__description";
    descEl.textContent = current.description;
    headerEl.appendChild(descEl);
  }
  sectionEl.appendChild(headerEl);

  const progressWrap = doc.createElement("div");
  progressWrap.className = "polst-campaign__progress";
  const progressBar = doc.createElement("div");
  progressBar.className = "polst-campaign__progress-bar";
  progressBar.setAttribute("role", "progressbar");
  progressBar.setAttribute("aria-label", "Campaign progress");
  progressBar.setAttribute("aria-valuemin", "0");
  progressBar.setAttribute("aria-valuemax", String(state.totalSteps));
  const progressFill = doc.createElement("div");
  progressFill.className = "polst-campaign__progress-fill";
  progressFill.style.width = "0%";
  progressBar.appendChild(progressFill);
  progressWrap.appendChild(progressBar);
  const progressLabel = doc.createElement("div");
  progressLabel.className = "polst-campaign__progress-label";
  const progressStep = doc.createElement("span");
  progressStep.textContent = `Step 0 of ${state.totalSteps}`;
  const progressPct = doc.createElement("span");
  progressPct.textContent = "0%";
  progressLabel.appendChild(progressStep);
  progressLabel.appendChild(progressPct);
  progressWrap.appendChild(progressLabel);
  sectionEl.appendChild(progressWrap);

  // Banner slot (closed, progress error). One banner at a time.
  let bannerEl: HTMLElement | null = null;

  // Stage — card container + overlay for "Next" button in manual mode.
  const stageEl = doc.createElement("div");
  stageEl.className = "polst-campaign__stage";
  sectionEl.appendChild(stageEl);

  widgetContainer.appendChild(sectionEl);

  function renderBanner(kind: "closed" | "progressError" | null): void {
    if (bannerEl && bannerEl.parentNode) bannerEl.parentNode.removeChild(bannerEl);
    bannerEl = null;
    if (!kind) return;
    const banner = doc.createElement("div");
    banner.className = "polst-campaign__banner";
    if (kind === "closed") {
      banner.textContent = "This campaign has closed. Voting is no longer available.";
      banner.setAttribute("data-kind", "closed");
    } else {
      banner.classList.add("polst-campaign__banner--warn");
      banner.textContent = "Couldn't restore your progress — starting from the top.";
      banner.setAttribute("data-kind", "progressError");
    }
    headerEl.after(banner);
    bannerEl = banner;
  }

  function updateProgressBar(): void {
    const pct = percent(state.completedSteps, state.totalSteps);
    progressFill.style.width = `${pct}%`;
    progressBar.setAttribute("aria-valuenow", String(state.completedSteps));
    progressStep.textContent =
      state.status === "complete"
        ? `Completed ${state.completedSteps} of ${state.totalSteps}`
        : `Step ${Math.min(state.stepIndex + 1, state.totalSteps)} of ${state.totalSteps}`;
    progressPct.textContent = `${pct}%`;
  }

  function clearStage(): void {
    if (cardHandle) {
      cardHandle.destroy();
      cardHandle = null;
    }
    while (stageEl.firstChild) stageEl.removeChild(stageEl.firstChild);
  }

  function mountStepCard(polst: PolstCardDTO): void {
    clearStage();

    const cardLayer = doc.createElement("div");
    cardLayer.className = "polst-campaign__card-layer";
    cardLayer.setAttribute("data-step-index", String(state.stepIndex));
    if (!reducedMotion) cardLayer.setAttribute("data-transitioning", "true");
    stageEl.appendChild(cardLayer);

    // The polst-card primitive expects a ShadowRoot, but it only uses
    // `appendChild` + `querySelector` on the handed-in root plus a
    // `[data-polst-widget-root]` lookup. We reuse the campaign's shadow
    // root but supply a throwaway stage container by wrapping cardLayer
    // in a minimal root-like shim.
    const cardShadowShim = makeCardShim(cardLayer, doc, root);

    const dto = cloneCardDTO(polst);
    cardHandle = renderPolstCard(cardShadowShim, dto, {
      vote: (polstId, option) => voteStep(polstId, option),
      hideBrand: options.hideBrand,
      hideShare: options.hideShare,
      document: options.document,
      now: options.now,
      onVote: (event) => handleStepVote(event),
      onVoteError: (err) => {
        // The card renders its own error block; we don't escalate.
        // Callers that want vote-error telemetry subscribe to the card.
        // Stashed for diagnostics in state.error if nothing else captures.
        state = { ...state, error: err };
      },
    });

    // Force the opacity transition on next frame. jsdom has no rAF in
    // older builds, so we fall back to a microtask.
    const raf =
      (doc.defaultView as unknown as { requestAnimationFrame?: (cb: () => void) => number } | null)
        ?.requestAnimationFrame;
    const runTransition = (): void => {
      if (destroyed) return;
      cardLayer.removeAttribute("data-transitioning");
    };
    if (reducedMotion) {
      cardLayer.removeAttribute("data-transitioning");
    } else if (typeof raf === "function") {
      raf(runTransition);
    } else {
      Promise.resolve().then(runTransition);
    }

    // Announce + optionally move focus. The announcement is synchronous
    // so tests + SR can observe it immediately after mount; focus moves
    // on the next microtask so the card's click handlers have installed.
    liveRegion.announce(
      `Step ${state.stepIndex + 1} of ${state.totalSteps}. ${polst.title}.`,
    );
    if (focusOnNextMount) {
      focusOnNextMount = false;
      const moveFocus = (): void => {
        if (destroyed) return;
        focusFirstInteractive(cardLayer);
      };
      if (typeof raf === "function") {
        raf(moveFocus);
      } else {
        Promise.resolve().then(moveFocus);
      }
    }
  }

  function mountNextButton(): void {
    const existing = stageEl.querySelector(".polst-campaign__next");
    if (existing) existing.remove();
    const btn = doc.createElement("button");
    btn.type = "button";
    btn.className = "polst-campaign__next";
    btn.setAttribute("data-action", "next");
    btn.textContent =
      state.stepIndex + 1 >= state.totalSteps ? "See summary" : "Next polst";
    btn.addEventListener("click", () => handleNextClick());
    stageEl.appendChild(btn);
  }

  function mountSummary(): void {
    clearStage();

    const wrap = doc.createElement("div");
    wrap.className = "polst-campaign__summary";
    wrap.setAttribute("data-complete", "true");

    const h = doc.createElement("h3");
    h.className = "polst-campaign__summary-title";
    h.textContent = "Your choices";
    wrap.appendChild(h);

    current.polsts.forEach((polst, idx) => {
      const row = doc.createElement("div");
      row.className = "polst-campaign__summary-row";
      row.setAttribute("data-polst-id", polst.shortId);

      const index = doc.createElement("span");
      index.className = "polst-campaign__summary-index";
      index.textContent = String(idx + 1);
      row.appendChild(index);

      const main = doc.createElement("div");
      main.className = "polst-campaign__summary-main";
      const titleP = doc.createElement("p");
      titleP.className = "polst-campaign__summary-polst-title";
      titleP.textContent = polst.title;
      main.appendChild(titleP);

      const choice = choices.get(polst.shortId) ?? null;
      const choiceP = doc.createElement("p");
      choiceP.className = "polst-campaign__summary-choice";
      if (choice) {
        const label =
          choice === "A" ? polst.optionA.label ?? "A" : polst.optionB.label ?? "B";
        choiceP.textContent = `You picked ${label}`;
      } else {
        choiceP.classList.add("polst-campaign__summary-choice--skipped");
        choiceP.textContent = "No vote recorded";
      }
      choiceP.setAttribute("data-choice", choice ?? "none");
      main.appendChild(choiceP);
      row.appendChild(main);

      const tally = doc.createElement("span");
      tally.className = "polst-campaign__summary-tally";
      const pctA = percent(polst.tallies.optionA, polst.tallies.total);
      const pctB = 100 - pctA;
      tally.textContent = `A ${pctA}% · B ${pctB}%`;
      row.appendChild(tally);

      wrap.appendChild(row);
    });

    if (!options.hideShare) {
      const share = doc.createElement("button");
      share.type = "button";
      share.className = "polst-campaign__share";
      share.setAttribute("data-action", "share");
      share.textContent = "Share this campaign";
      share.addEventListener("click", () => handleShare());
      wrap.appendChild(share);
    }

    stageEl.appendChild(wrap);
  }

  function handleShare(): void {
    const appOrigin = getAppOrigin();
    const shareUrl = current.brand
      ? `${appOrigin}/${current.brand.slug}/c/${current.campaignId}`
      : `${appOrigin}/c/${current.campaignId}`;
    const nav =
      (doc.defaultView as unknown as {
        navigator?: { share?: (d: ShareData) => Promise<void> };
      } | null)?.navigator;
    if (nav?.share) {
      void nav.share({ title: current.title, url: shareUrl });
    }
  }

  function handleStepVote(event: { polstId: string; option: "A" | "B" }): void {
    if (destroyed) return;
    // Revote path — the same polstId may already be completed. We do
    // not double-count, but we DO re-fire `onStepVote` so embedders can
    // re-sync their own UI.
    const wasCompleted = completedPolstIds.has(event.polstId);
    completedPolstIds.add(event.polstId);
    choices.set(event.polstId, event.option);
    if (!wasCompleted) {
      state = { ...state, completedSteps: state.completedSteps + 1 };
    }
    updateProgressBar();

    options.onStepVote?.({
      campaignId: current.campaignId,
      polstId: event.polstId,
      option: event.option,
      stepIndex: state.stepIndex,
      totalSteps: state.totalSteps,
      completedSteps: state.completedSteps,
    });

    if (state.completedSteps >= state.totalSteps) {
      advanceToSummary();
      return;
    }

    if (autoAdvance) {
      advanceToNextUncompleted();
    } else {
      state = { ...state, status: "awaitingNext" };
      mountNextButton();
    }
  }

  function handleNextClick(): void {
    if (destroyed) return;
    if (state.completedSteps >= state.totalSteps) {
      advanceToSummary();
    } else {
      advanceToNextUncompleted();
    }
  }

  function nextUncompletedIndex(from: number): number {
    for (let i = from; i < current.polsts.length; i += 1) {
      const polst = current.polsts[i];
      if (polst && !completedPolstIds.has(polst.shortId)) return i;
    }
    for (let i = 0; i < from; i += 1) {
      const polst = current.polsts[i];
      if (polst && !completedPolstIds.has(polst.shortId)) return i;
    }
    return -1;
  }

  function advanceToNextUncompleted(): void {
    const idx = nextUncompletedIndex(state.stepIndex + 1);
    if (idx === -1) {
      advanceToSummary();
      return;
    }
    const nextPolst = current.polsts[idx];
    if (!nextPolst) {
      advanceToSummary();
      return;
    }
    state = { ...state, status: "playing", stepIndex: idx };
    updateProgressBar();
    focusOnNextMount = true;
    mountStepCard(nextPolst);
  }

  function advanceToSummary(): void {
    state = { ...state, status: "complete" };
    updateProgressBar();
    mountSummary();
    liveRegion.announce(
      `Campaign complete. ${state.completedSteps} of ${state.totalSteps} choices recorded.`,
    );
    options.onComplete?.({
      campaignId: current.campaignId,
      totalSteps: state.totalSteps,
      completedAt: now().toISOString(),
      choices: current.polsts.map((p) => ({
        polstId: p.shortId,
        option: choices.get(p.shortId) ?? null,
      })),
    });
  }

  function applyClosedIfNeeded(): boolean {
    if (current.status !== "closed") return false;
    renderBanner("closed");
    state = { ...state, status: "closed" };
    // Render the current polst as a closed card so the tally still
    // shows but the vote buttons disappear.
    const idx = Math.max(0, Math.min(state.stepIndex, current.polsts.length - 1));
    if (current.polsts[idx]) {
      const closed: PolstCardDTO = { ...current.polsts[idx], status: "closed" };
      mountStepCard(closed);
    }
    return true;
  }

  function applyProgress(progress: CampaignPlayerProgress | null): void {
    if (destroyed) return;
    completedPolstIds.clear();
    const completed = progress?.completedSteps ?? 0;
    // We don't know WHICH polsts are completed from the DTO directly
    // when `completed < totalSteps`, only how many. But `currentPolst`
    // tells us the next uncompleted one, so everything before it is
    // completed and everything from it onward is not.
    if (progress && !progress.completed && progress.currentPolst) {
      const currentIdx = current.polsts.findIndex(
        (p) => p.shortId === progress.currentPolst!.shortId,
      );
      const nextIdx = currentIdx === -1 ? 0 : currentIdx;
      for (let i = 0; i < nextIdx; i += 1) {
        const polst = current.polsts[i];
        if (polst) completedPolstIds.add(polst.shortId);
      }
      state = {
        ...state,
        status: "playing",
        stepIndex: nextIdx,
        completedSteps: Math.max(completed, completedPolstIds.size),
      };
    } else if (progress?.completed) {
      for (const p of current.polsts) completedPolstIds.add(p.shortId);
      state = {
        ...state,
        status: "complete",
        stepIndex: current.polsts.length,
        completedSteps: current.polsts.length,
      };
    } else {
      // No progress info (or fallback): start at step 0 with 0 done.
      state = { ...state, status: "playing", stepIndex: 0, completedSteps: 0 };
    }

    if (applyClosedIfNeeded()) return;

    updateProgressBar();

    if (state.status === "complete") {
      mountSummary();
      options.onComplete?.({
        campaignId: current.campaignId,
        totalSteps: state.totalSteps,
        completedAt: progress?.summary?.completedAt ?? now().toISOString(),
        choices: current.polsts.map((p) => ({
          polstId: p.shortId,
          option: choices.get(p.shortId) ?? null,
        })),
      });
      return;
    }

    if (current.polsts.length === 0) {
      // Degenerate: campaign with no polsts. Surface a banner + empty
      // stage; there is nothing to render.
      renderBanner("progressError");
      return;
    }
    const stepPolst = current.polsts[state.stepIndex];
    if (!stepPolst) {
      renderBanner("progressError");
      return;
    }
    mountStepCard(stepPolst);
  }

  // ─── Kick off ───────────────────────────────────────────────────────────

  // Initial paint.
  updateProgressBar();

  // Closed at mount — short-circuit the fetch.
  if (current.status === "closed") {
    state = { ...state, status: "closed", totalSteps: current.polsts.length };
    updateProgressBar();
    renderBanner("closed");
    const firstPolst = current.polsts[0];
    if (firstPolst) {
      const closed: PolstCardDTO = { ...firstPolst, status: "closed" };
      mountStepCard(closed);
    }
  } else {
    // Fire the progress fetch. A rejection falls back to step 0 with a
    // banner — the user can still play.
    void (async () => {
      try {
        const progress = await fetchProgress(current.campaignId);
        if (destroyed) return;
        applyProgress(progress);
      } catch (err) {
        if (destroyed) return;
        renderBanner("progressError");
        options.onProgressError?.(err);
        applyProgress(null);
      }
    })();
  }

  // ─── Handle ─────────────────────────────────────────────────────────────

  return {
    getState(): CampaignPlayerState {
      return {
        status: state.status,
        stepIndex: state.stepIndex,
        totalSteps: state.totalSteps,
        completedSteps: state.completedSteps,
        error: state.error,
      };
    },
    goTo(targetIndex: number): void {
      if (destroyed) return;
      if (
        !Number.isInteger(targetIndex) ||
        targetIndex < 0 ||
        targetIndex >= current.polsts.length
      ) {
        return;
      }
      if (state.status === "closed") return;
      const targetPolst = current.polsts[targetIndex];
      if (!targetPolst) return;
      state = { ...state, status: "playing", stepIndex: targetIndex };
      updateProgressBar();
      focusOnNextMount = true;
      mountStepCard(targetPolst);
    },
    update(next: CampaignPlayerDTO): void {
      if (destroyed) return;
      current = cloneCampaign(next);
      state = {
        ...state,
        totalSteps: current.polsts.length,
      };
      titleEl.textContent = current.title;
      progressBar.setAttribute("aria-valuemax", String(state.totalSteps));

      if (applyClosedIfNeeded()) return;

      if (state.status === "complete") {
        mountSummary();
      } else {
        const idx = Math.max(0, Math.min(state.stepIndex, current.polsts.length - 1));
        state = { ...state, stepIndex: idx };
        if (current.polsts[idx]) mountStepCard(current.polsts[idx]);
      }
      updateProgressBar();
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      clearStage();
      if (sectionEl.parentNode) sectionEl.parentNode.removeChild(sectionEl);
    },
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function cloneCampaign(c: CampaignPlayerDTO): CampaignPlayerDTO {
  return {
    ...c,
    polsts: c.polsts.map(cloneCardDTO),
    brand: c.brand ? { ...c.brand } : null,
  };
}

function cloneCardDTO(p: PolstCardDTO): PolstCardDTO {
  return {
    ...p,
    optionA: { ...p.optionA },
    optionB: { ...p.optionB },
    tallies: { ...p.tallies },
    brand: p.brand ? { ...p.brand } : null,
  };
}

/**
 * Build a minimal shadow-root-like facade that `renderPolstCard`
 * accepts. The card only needs `appendChild` + `querySelector` +
 * `ownerDocument`, which we proxy to a plain `Element` stage container
 * while `[data-polst-widget-root]` lookups continue to resolve through
 * the real shadow root's children.
 */
function makeCardShim(
  stageLayer: Element,
  doc: Document,
  realRoot: ShadowRoot,
): ShadowRoot {
  // Give the card its own `[data-polst-widget-root]` inside the stage
  // layer so its `findWidgetContainer()` resolves locally.
  const innerContainer = doc.createElement("div");
  innerContainer.setAttribute("data-polst-widget-root", "");
  stageLayer.appendChild(innerContainer);

  // Typed as ShadowRoot for the card's runtime check, but we only
  // implement what the card actually touches.
  const shim = {
    appendChild(node: Node): Node {
      // Style element lookup — the card installs its <style> once per
      // root. Route <style> writes to the real shadow root so every
      // campaign step shares one stylesheet (the card gates on the
      // STYLE_MARKER attribute existing anywhere in the root).
      if ((node as Element).tagName?.toLowerCase() === "style") {
        return realRoot.appendChild(node);
      }
      return stageLayer.appendChild(node);
    },
    querySelector(selector: string): Element | null {
      // The card probes `style[data-polst-card-styles]` + the widget
      // root container. We delegate the style probe to the real root,
      // and the widget-root probe to the per-layer container.
      if (selector.startsWith("style[")) {
        return realRoot.querySelector(selector);
      }
      return stageLayer.querySelector(selector);
    },
    get ownerDocument(): Document {
      return doc;
    },
  };

  return shim as unknown as ShadowRoot;
}
